import React, { useState } from 'react';
import { TeilnahmeRahmen } from '../../sifa/public/TeilnahmeRahmen';
import { FEHLERKASTEN, HINWEISKASTEN } from '../../sifa/public/teilnahmeSeiteStil';
import { useFreimeldung } from './useFreimeldung';
import { formatDatum, planAnsichtenFuer, planKnopfText } from './freimeldungLogic';
import { FreimeldungListe } from './FreimeldungListe';
import { FreimeldungFormular } from './FreimeldungFormular';
import { FreimeldungErgebnis } from './FreimeldungErgebnis';
import { FreimeldungPlanAnsicht } from './FreimeldungPlanAnsicht';

/**
 * Oeffentliche Melderseite #/freimeldung/<token> — die Empfaenger des
 * Begehungsprotokolls melden ihre Maengel per QR/Link als behoben
 * (docs/freimeldung-online-plan.md §E).
 *
 * Wird OHNE Login gerendert (index.tsx routet am Auth-Gate vorbei, wie
 * #/einweisung). Duenner Container: der Zustand lebt in useFreimeldung, die
 * Regeln in freimeldungLogic; hier wird nur der aktuelle Schritt auf die
 * passende Ansicht abgebildet.
 *
 * Die Meldung SCHLIESST den Mangel NICHT — sie legt eine offene Freimeldung
 * an, ueber die der Koordinator entscheidet.
 */
export const FreimeldungPage: React.FC<{ token: string }> = ({ token }) => {
  const f = useFreimeldung(token);
  const t = f.texte;
  // Mangel, dessen Plan gerade offen ist (reine Anzeige, kein Seitenzustand).
  const [planFuer, setPlanFuer] = useState<string | null>(null);

  if (f.schritt === 'laedt') {
    return (
      <TeilnahmeRahmen>
        <p className="text-slate-500" aria-live="polite">
          {t.ladeText}
        </p>
      </TeilnahmeRahmen>
    );
  }

  // Keine Angabe, ob es den Bericht gibt — unbekannter Token, abgelaufener
  // Link und fehlende Migration bekommen denselben Satz.
  if (f.schritt === 'ungueltig') {
    return (
      <TeilnahmeRahmen>
        <h1 className="text-xl font-bold text-slate-900">{t.ungueltigTitel}</h1>
        <div className={HINWEISKASTEN}>{t.ungueltig}</div>
      </TeilnahmeRahmen>
    );
  }

  if (f.schritt === 'fehler' || !f.daten) {
    return (
      <TeilnahmeRahmen>
        <h1 className="text-xl font-bold text-slate-900">{t.ladeFehlerTitel}</h1>
        {f.ladeFehler && <div className={FEHLERKASTEN}>{f.ladeFehler}</div>}
        <p className="text-slate-600">{t.ladeFehlerHinweis}</p>
      </TeilnahmeRahmen>
    );
  }

  const d = f.daten;
  const sendet = f.schritt === 'sendet';
  const imFormular = (f.schritt === 'formular' || sendet) && !!f.ausgewaehlt;

  const planItem = planFuer
    ? (f.items.find((i) => i.id === planFuer) ?? (f.ausgewaehlt?.id === planFuer ? f.ausgewaehlt : null))
    : null;
  const planAnsichten = planItem ? planAnsichtenFuer(planItem, d.plaene) : [];
  const formularPlanKnopf = f.ausgewaehlt
    ? planKnopfText(planAnsichtenFuer(f.ausgewaehlt, d.plaene).length, f.sprache)
    : '';

  let inhalt: React.ReactNode;
  if (f.schritt === 'ergebnis' && f.ergebnis) {
    inhalt = (
      <FreimeldungErgebnis
        ergebnis={f.ergebnis}
        texte={t}
        sprache={f.sprache}
        onWeiterenMelden={f.weiterenMelden}
      />
    );
  } else if (imFormular && f.ausgewaehlt) {
    inhalt = (
      <FreimeldungFormular
        item={f.ausgewaehlt}
        art={f.art}
        formular={f.formular}
        firmen={f.firmen}
        texte={t}
        sprache={f.sprache}
        fotoPflicht={f.fotoPflicht}
        fotoLaedt={f.fotoLaedt}
        sendet={sendet}
        fehler={f.fehler}
        feldFehler={f.feldFehler}
        onFeld={f.setzeFeld}
        onFoto={(datei) => void f.fotoWaehlen(datei)}
        onFotoEntfernen={f.fotoEntfernen}
        onAbsenden={() => void f.absenden()}
        onZurueck={f.zurueckZurListe}
        planKnopf={formularPlanKnopf}
        onPlan={() => setPlanFuer(f.ausgewaehlt?.id ?? null)}
      />
    );
  } else {
    // Liste — auch dann, wenn ein gewaehlter Mangel zwischenzeitlich
    // verschwunden ist (der Koordinator hat ihn schon geschlossen).
    inhalt = (
      <FreimeldungListe
        items={f.sichtbareItems}
        gesamt={f.items.length}
        plaene={d.plaene}
        texte={t}
        sprache={f.sprache}
        firmenKnoepfe={f.firmenFilterKnoepfe}
        firmaFilter={f.firmaFilter}
        onFirmaFilter={f.waehleFirmaFilter}
        onWaehlen={f.waehleItem}
        onPlan={setPlanFuer}
      />
    );
  }

  return (
    <TeilnahmeRahmen>
      <header className="space-y-1 pb-4 border-b border-slate-200">
        {d.bueroName && <p className="text-sm font-medium text-slate-500">{d.bueroName}</p>}
        <h1 className="text-2xl font-bold text-slate-900">{imFormular && f.art === 'zurueckgewiesen' ? t.formularTitelZurueck : t.titel}</h1>
        {d.projektName && <p className="font-semibold text-slate-800 break-words">{d.projektName}</p>}
        {d.projektAdresse && <p className="text-sm text-slate-600 break-words">{d.projektAdresse}</p>}
        <p className="text-sm text-slate-500">{t.bericht(d.berichtNr, formatDatum(d.berichtDatum, f.sprache))}</p>
      </header>

      {inhalt}

      {planItem && planAnsichten.length > 0 && (
        <FreimeldungPlanAnsicht
          item={planItem}
          ansichten={planAnsichten}
          texte={t}
          onSchliessen={() => setPlanFuer(null)}
        />
      )}
    </TeilnahmeRahmen>
  );
};

export default FreimeldungPage;
